import { buildContextMeta } from '../constants/intelligenceContexts';

/** Parse ranges like "-18°C to 4°C" or "2°C to 8°C". */
export function parseTempRange(tempRange) {
  if (!tempRange || tempRange === 'Custom') return null;
  const match = String(tempRange).match(/(-?\d+(?:\.\d+)?)\s*°?C?\s*to\s*(-?\d+(?:\.\d+)?)/i);
  if (!match) return null;
  return { min: parseFloat(match[1]), max: parseFloat(match[2]) };
}

export function assessTempAgainstContext(temp, tempRange) {
  const range = parseTempRange(tempRange);
  if (range == null || temp == null || Number.isNaN(temp)) return 'unknown';
  if (temp < range.min) return 'below';
  if (temp > range.max) return 'above';
  return 'in_range';
}

export function attachIntelligenceContext(device, contextRow) {
  const intelligenceContext = buildContextMeta(contextRow);
  return {
    ...device,
    intelligenceContext,
    hasIntelligenceContext: Boolean(intelligenceContext),
  };
}

export function enrichDevicesWithContexts(devices, contextMap = {}) {
  return devices.map((device) => {
    const contextRow = contextMap[device.id];
    return attachIntelligenceContext(device, contextRow);
  });
}

export function buildIntelligenceInstruction(devices) {
  const withContext = devices.filter((d) => d.hasIntelligenceContext);
  const withoutContext = devices.filter((d) => !d.hasIntelligenceContext);

  let instruction =
    'Analyze sensor devices for a cold-chain monitoring dashboard. ' +
    'Each device may include an intelligenceContext (use case such as Butchers, Cold Chain, Pharmaceuticals) ' +
    'with expected temperature range and custom notes. ' +
    'Interpret readings and status IN LIGHT OF that context — explain compliance risk, spoilage risk, and operational meaning. ' +
    'Reference device names, context labels, expected ranges, and actual temperature/humidity readings.';

  if (withContext.length) {
    instruction += ` ${withContext.length} device(s) have intelligence context assigned.`;
  }
  if (withoutContext.length) {
    instruction += ` ${withoutContext.length} device(s) have no context — note this and suggest assigning context on the Intelligence page.`;
  }

  return instruction;
}

export function summarizeContextsUsed(devices) {
  return devices
    .filter((d) => d.intelligenceContext)
    .map((d) => ({
      deviceId: d.id,
      deviceName: d.name,
      contextLabel: d.intelligenceContext.label,
      contextIcon: d.intelligenceContext.icon,
      tempRange: d.intelligenceContext.tempRange,
      critical: d.intelligenceContext.critical,
      customNotes: d.intelligenceContext.customNotes,
      temperature: d.temperature ?? d.lastReading?.temperature,
      humidity: d.humidity ?? d.lastReading?.humidity,
      status: d.status,
      tempAssessment: assessTempAgainstContext(
        d.temperature ?? d.lastReading?.temperature,
        d.intelligenceContext.tempRange,
      ),
    }));
}

export function analyzeDevicesWithContext(devices) {
  const n = devices.length;
  const statusCounts = { online: 0, warning: 0, offline: 0, unknown: 0 };
  const withContext = devices.filter((d) => d.hasIntelligenceContext);
  const withoutContext = devices.filter((d) => !d.hasIntelligenceContext);
  const contextGroups = {};
  const insights = [];
  const recommendations = [];

  devices.forEach((d) => {
    const status = (d.status || 'unknown').toLowerCase();
    statusCounts[status] = (statusCounts[status] ?? 0) + 1;

    const ctx = d.intelligenceContext;
    if (!ctx) return;

    const label = ctx.label;
    contextGroups[label] = (contextGroups[label] || 0) + 1;

    const temp = d.temperature ?? d.lastReading?.temperature;
    const assessment = assessTempAgainstContext(temp, ctx.tempRange);
    const name = d.name || d.id;

    if (status === 'offline') {
      insights.push(
        `${name} (${label}): offline — no recent data for this ${ctx.critical ? 'critical' : 'monitored'} use case.`,
      );
      recommendations.push(
        `Restore connectivity for ${name} (${label}) to maintain ${ctx.tempRange} monitoring.`,
      );
    } else if (assessment === 'above' && temp != null) {
      insights.push(
        `${name} (${label}): ${temp.toFixed(1)}°C is above the expected ${ctx.tempRange} range — spoilage/compliance risk.`,
      );
      recommendations.push(
        `Check cooling for ${name} in ${label} context; target range is ${ctx.tempRange}.`,
      );
    } else if (assessment === 'below' && temp != null) {
      insights.push(
        `${name} (${label}): ${temp.toFixed(1)}°C is below the expected ${ctx.tempRange} range.`,
      );
      recommendations.push(
        `Verify setpoints for ${name} (${label}); expected ${ctx.tempRange}.`,
      );
    } else if (assessment === 'in_range' && temp != null) {
      insights.push(
        `${name} (${label}): ${temp.toFixed(1)}°C is within expected ${ctx.tempRange} for this use case.`,
      );
    }
  });

  const contextSummary = Object.entries(contextGroups)
    .map(([label, count]) => `${label}: ${count}`)
    .join('; ');

  let analysis = `Analyzed ${n} device${n !== 1 ? 's' : ''}. `;
  if (withContext.length) {
    analysis += `${withContext.length} have intelligence context (${contextSummary || 'assigned'}). `;
  }
  if (withoutContext.length) {
    analysis += `${withoutContext.length} lack context — assign on Intelligence for context-aware insights. `;
  }
  analysis += `Status: ${statusCounts.online || 0} online, ${statusCounts.warning || 0} warning, ${statusCounts.offline || 0} offline.`;

  if (!withContext.length && n > 0) {
    insights.push('No intelligence context assigned — analysis is limited to connectivity status only.');
    recommendations.push('Open Intelligence and assign a context (e.g. Butchers, Cold Chain) per sensor for compliance-focused insights.');
  }

  if (!insights.length && n > 0) {
    insights.push('No context-specific issues detected from the current snapshot.');
  }
  if (!recommendations.length && n > 0) {
    recommendations.push('Continue monitoring against each device’s assigned intelligence context.');
  }

  return {
    analysis: analysis.trim(),
    confidence: withContext.length ? 0.9 : 0.75,
    insights,
    recommendations,
    contextsUsed: summarizeContextsUsed(devices),
    devicesWithoutContext: withoutContext.map((d) => d.name || d.id),
  };
}

function getSensorStatusFromReading(latestReading) {
  if (!latestReading) return 'offline';
  const timestamp = latestReading?.created_at || latestReading?.recorded_at;
  if (!timestamp) return 'offline';
  const diffMins = Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
  if (diffMins > 60) return 'offline';
  if (diffMins > 15) return 'warning';
  return 'online';
}

/** Attach connected sensors (with intelligence context) to each gateway. */
export function enrichGatewaysWithContexts(gateways = [], sensors = [], contextMap = {}) {
  const sensorsByGateway = {};

  sensors.forEach((sensor) => {
    const gatewayId = sensor.gateway_id;
    if (!gatewayId) return;

    const reading = sensor.latest_reading;
    const enriched = attachIntelligenceContext(
      {
        id: sensor.id,
        name: sensor.name,
        status: getSensorStatusFromReading(reading),
        temperature: reading?.temperature,
        humidity: reading?.humidity,
        lastReading: reading,
      },
      contextMap[sensor.id],
    );

    if (!sensorsByGateway[gatewayId]) sensorsByGateway[gatewayId] = [];
    sensorsByGateway[gatewayId].push(enriched);
  });

  return gateways.map((gateway) => {
    const connectedSensors = sensorsByGateway[gateway.id] || [];
    const monitoredContexts = [
      ...new Set(
        connectedSensors
          .filter((s) => s.intelligenceContext)
          .map((s) => s.intelligenceContext.label),
      ),
    ];

    return {
      ...gateway,
      connectedSensors,
      monitoredContexts,
      hasIntelligenceContext: connectedSensors.some((s) => s.hasIntelligenceContext),
      intelligenceContextSummary: monitoredContexts.length ? monitoredContexts.join(', ') : null,
      sensorsWithoutContext: connectedSensors
        .filter((s) => !s.hasIntelligenceContext)
        .map((s) => s.name || s.id),
    };
  });
}

export function buildGatewayIntelligenceInstruction(gateways) {
  const withContext = gateways.filter((g) => g.hasIntelligenceContext);
  const withoutContextSensors = gateways.flatMap((g) => g.sensorsWithoutContext || []);

  let instruction =
    'Analyze gateways for a cold-chain monitoring dashboard. ' +
    'Each gateway includes connectedSensors with optional intelligenceContext (use case, expected temp range, notes). ' +
    'Interpret gateway connectivity AND what it means for sensors in each context — compliance risk, spoilage risk, blind spots. ' +
    'Reference gateway names, monitored context labels, connected sensor counts, and sensor readings.';

  if (withContext.length) {
    instruction += ` ${withContext.length} gateway(s) monitor sensors with intelligence context.`;
  }
  if (withoutContextSensors.length) {
    instruction += ` ${withoutContextSensors.length} connected sensor(s) lack context — recommend assigning on Intelligence page.`;
  }

  return instruction;
}

export function summarizeGatewayContextsUsed(gateways) {
  const rows = [];
  gateways.forEach((gateway) => {
    (gateway.connectedSensors || []).forEach((sensor) => {
      if (!sensor.intelligenceContext) return;
      rows.push({
        gatewayName: gateway.name || gateway.mac_address,
        deviceName: sensor.name,
        contextLabel: sensor.intelligenceContext.label,
        contextIcon: sensor.intelligenceContext.icon,
        tempRange: sensor.intelligenceContext.tempRange,
        critical: sensor.intelligenceContext.critical,
        customNotes: sensor.intelligenceContext.customNotes,
        temperature: sensor.temperature,
        humidity: sensor.humidity,
        status: sensor.status,
        tempAssessment: assessTempAgainstContext(
          sensor.temperature,
          sensor.intelligenceContext.tempRange,
        ),
      });
    });
  });
  return rows;
}

export function analyzeGatewaysWithContext(gateways) {
  const n = gateways.length;
  const statusCounts = { online: 0, warning: 0, offline: 0, unknown: 0 };
  const insights = [];
  const recommendations = [];
  const contextsUsed = summarizeGatewayContextsUsed(gateways);
  const sensorsWithoutContext = gateways.flatMap((g) => g.sensorsWithoutContext || []);

  gateways.forEach((gateway) => {
    const status = (gateway.status || gateway.connectivity || 'unknown').toLowerCase();
    statusCounts[status] = (statusCounts[status] ?? 0) + 1;
    const name = gateway.name || gateway.mac_address || gateway.id;
    const contexts = gateway.monitoredContexts || [];
    const connected = gateway.connectedSensors || [];
    const criticalSensors = connected.filter((s) => s.intelligenceContext?.critical);

    if (status === 'offline') {
      if (criticalSensors.length) {
        insights.push(
          `${name} is offline — ${criticalSensors.length} critical-context sensor(s) (${contexts.join(', ')}) may be unmonitored.`,
        );
        recommendations.push(
          `Restore ${name} immediately; critical cold-chain contexts depend on this gateway.`,
        );
      } else if (contexts.length) {
        insights.push(`${name} is offline — monitoring paused for ${contexts.join(', ')} sensors.`);
        recommendations.push(`Check power and network for gateway ${name}.`);
      } else {
        insights.push(`${name} is offline.`);
        recommendations.push(`Troubleshoot connectivity for gateway ${name}.`);
      }
    } else if (contexts.length) {
      insights.push(
        `${name} is ${status} monitoring ${connected.length} sensor(s) across: ${contexts.join(', ')}.`,
      );
    }

    connected.forEach((sensor) => {
      const ctx = sensor.intelligenceContext;
      if (!ctx || sensor.status === 'offline') return;
      const temp = sensor.temperature;
      const assessment = assessTempAgainstContext(temp, ctx.tempRange);
      if (assessment === 'above' && temp != null) {
        insights.push(
          `Via ${name}: ${sensor.name} (${ctx.label}) at ${temp.toFixed(1)}°C exceeds ${ctx.tempRange}.`,
        );
      } else if (assessment === 'below' && temp != null) {
        insights.push(
          `Via ${name}: ${sensor.name} (${ctx.label}) at ${temp.toFixed(1)}°C below ${ctx.tempRange}.`,
        );
      }
    });
  });

  const gatewaysWithContext = gateways.filter((g) => g.hasIntelligenceContext).length;
  let analysis = `Analyzed ${n} gateway${n !== 1 ? 's' : ''}. `;
  analysis += `Connectivity: ${statusCounts.online || 0} online, ${statusCounts.warning || 0} warning, ${statusCounts.offline || 0} offline. `;
  if (gatewaysWithContext) {
    analysis += `${gatewaysWithContext} gateway(s) monitor intelligence-context sensors. `;
  }
  if (sensorsWithoutContext.length) {
    analysis += `${sensorsWithoutContext.length} connected sensor(s) lack context — assign on Intelligence for richer analysis.`;
  }

  if (!insights.length && n > 0) {
    insights.push('No gateway or context-specific issues detected from the current snapshot.');
  }
  if (!recommendations.length && n > 0) {
    recommendations.push('Continue monitoring gateway health and context-assigned sensors.');
  }

  return {
    analysis: analysis.trim(),
    confidence: contextsUsed.length ? 0.9 : 0.75,
    insights,
    recommendations,
    contextsUsed,
    devicesWithoutContext: sensorsWithoutContext,
  };
}
